<script setup lang="ts">
// Train my voice — dedicated page (round-16, 2026-08-31 user request: "separate page or dialog,
// your pick" — a page, reached from Player.vue, because it needs real vertical room for the
// reading text + progress bar + controls all at once, more than a dialog comfortably fits at
// 960x540). Round-17 (2026-08-31) rewrite per user feedback after testing round-16: one merged
// panel instead of two, a state machine gating recognition/enrollment behind an explicit Start
// (recognition used to run live before Start was pressed), a boring/neutral reading text split
// into short sentences with a Next flow, red mismatch feedback, and a silence/noise baseline
// capture step at the start of the flow. See DESIGN.md §11 for the full writeup.
//
// This page owns a SECOND, independent WebSocket connection to kmb-stt (same pattern as
// DebugStt.vue) purely for the realtime visual feedback — audio level, live partial/final
// transcript, and (new) the noise-baseline snapshot request/response. The actual enrollment
// SAMPLES still flow through the app-wide connection in stt.ts (startEnrollment/stopEnrollment
// + its forwardSample POSTing), so there's exactly one pipeline writing reading samples to the
// corpus; this page's own socket only ever posts the noise-baseline sample itself (a one-off,
// not part of that pipeline — see postNoiseSample).
//
// State machine (round-17, "recognition must not run before Start" fix):
//   idle    — nothing captured, nothing matched, mic connection may be open but ignored.
//   noise   — "stay silent" baseline capture (a few seconds), then one voice_samples POST.
//   reading — sentences advance via a pointer-with-lookahead match (readingAid, not scoring);
//             mismatched heard words flash red with a "read the text" hint; Next unlocks once
//             the current sentence is (mostly) read.
//   done    — all sentences read; enrollment stopped; offers to train again.
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { mdiArrowLeft } from '@mdi/js'
import { getPlayer, rebuildVoiceProfile, getTrainingProgress, putTrainingProgress } from '../backend'
import { stt, startEnrollment, stopEnrollment } from '../stt'
import { postVoiceSample } from '../backend'
import { nav, navigate } from '../store/nav'
import type { PlayerDetail } from '../types'

type Lang = 'es' | 'en'
type Step = 'idle' | 'noise' | 'reading' | 'done'

const player = ref<PlayerDetail | null>(null)
const loadError = ref(false)

async function loadPlayer() {
  const id = nav.params.playerId
  if (id == null) {
    loadError.value = true
    return
  }
  try {
    player.value = await getPlayer(id)
  } catch {
    loadError.value = true
  }
}

function goBack() {
  navigate('player', { playerId: nav.params.playerId })
}

// ---------------------------------------------------------------------------------------------
// Own WS connection, purely for live level/transcript display + the noise-baseline request
// (see file header). Never posts reading samples itself.
const WS_URL = 'ws://127.0.0.1:9224'
const BACKOFF_START_MS = 1000
const BACKOFF_MAX_MS = 10000

const connectionState = ref<'connecting' | 'open' | 'closed'>('connecting')
const rms = ref(0)
const partialText = ref('')

let ws: WebSocket | null = null
let closedByUs = false
let backoff = BACKOFF_START_MS
let retryTimer: ReturnType<typeof setTimeout> | null = null

function handleEvent(ev: { type?: string; rms?: number; text?: string; lang?: Lang; audioB64?: string }) {
  if (ev.type === 'level' && typeof ev.rms === 'number') rms.value = ev.rms
  else if (ev.type === 'partial' && typeof ev.text === 'string') {
    // Ignored entirely outside 'reading' — the state machine's whole point (round-17) is that
    // recognition before Start must have no visible/stored effect. Item 1 (round-18): also
    // ignored when it's the OTHER language's parallel decode of the same audio — kmb-stt runs
    // es+en recognizers side by side, and only the expected-lang one is the trainee's actual
    // speech; the other is a parallel garbage decode of the same utterance, not a second
    // thing said. Left un-updated (not blanked) so a stray off-lang partial can't visibly
    // erase progress that's mid-way through matching.
    if (step.value === 'reading' && ev.lang === lang.value) partialText.value = ev.text
  } else if (ev.type === 'final' && typeof ev.text === 'string') {
    if (step.value === 'reading' && ev.lang === lang.value) {
      partialText.value = ''
      advanceReading(ev.text)
    }
    // else: off-language final (or outside 'reading') — dropped entirely, no red flash, no
    // pointer movement (item 1).
  } else if (ev.type === 'noise_sample' && typeof ev.audioB64 === 'string' && awaitingNoiseSample) {
    awaitingNoiseSample = false
    postNoiseSample(ev.audioB64)
    beginReadingStep()
  }
}

function connect() {
  if (closedByUs) return
  connectionState.value = 'connecting'
  ws = new WebSocket(WS_URL)
  ws.addEventListener('open', () => {
    backoff = BACKOFF_START_MS
    connectionState.value = 'open'
  })
  ws.addEventListener('message', (evt) => {
    try {
      handleEvent(JSON.parse(evt.data as string))
    } catch {
      /* malformed frame, next one self-corrects */
    }
  })
  ws.addEventListener('close', () => {
    connectionState.value = 'closed'
    ws = null
    if (closedByUs) return
    retryTimer = setTimeout(connect, backoff)
    backoff = Math.min(backoff * 2, BACKOFF_MAX_MS)
  })
  ws.addEventListener('error', () => ws?.close())
}

// ---------------------------------------------------------------------------------------------
// Guided reading — es-ES default, EN fallback. Round-17: rewritten to a BORING, flat,
// instruction-manual register (was playful/inciting — "bienvenidos", "gano yo", "muy animado" —
// which people read with an entertainer's cadence, biasing the voice profile away from how they
// actually talk day to day). Split into short, independent sentences (item 4) rather than one
// paragraph, so the flow can advance sentence by sentence with a Next control.
const SENTENCES: Record<Lang, string[]> = {
  es: [
    'El servicio de mantenimiento revisa el equipo una vez al mes.',
    'Introduzca la contraseña y pulse la tecla de confirmación.',
    'El documento debe entregarse antes de las cinco de la tarde.',
    'Guarde el archivo en la carpeta indicada por el sistema.',
    'La reunión se traslada a la sala número dos.',
  ],
  en: [
    'The maintenance team checks the equipment once a month.',
    'Enter the password and press the confirmation key.',
    'The document must be submitted before five in the afternoon.',
    'Save the file in the folder indicated by the system.',
    'The meeting has been moved to room number two.',
  ],
}

const lang = ref<Lang>('es')
function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip accents so "número" ~= "numero" matches heard text
    .replace(/[^a-z0-9\s]/g, '')
    .split(/\s+/)
    .filter(Boolean)
}

const step = ref<Step>('idle')
const sentenceIndex = ref(0)
const totalSentences = computed(() => SENTENCES[lang.value].length)
const sentenceWords = computed(() => SENTENCES[lang.value][sentenceIndex.value].split(/\s+/))
const sentenceTargetTokens = computed(() => sentenceWords.value.map((w) => tokenize(w)[0] ?? ''))
const readPointer = ref(0)
// How far ahead of the pointer to search for a match — tolerates a skipped/misheard word
// without permanently stalling (falls back to matching whatever comes next within this window).
const LOOKAHEAD = 4

// Item 2 (round-18 rework): ONE component — the sentence itself is the karaoke display, no
// separate transcript/mismatch strip. `matchForward` is the shared pointer-with-lookahead
// algorithm; finals COMMIT (advanceReading, moves readPointer permanently), partials only
// PREVIEW (tentativePointer below, non-destructive) so words visibly green progressively as
// the trainee speaks, not just in one jump when the utterance finalizes. A mismatch (heard
// word that doesn't fit the lookahead window) is shown as a brief shake + red tint on the
// not-yet-read words plus the standing "read the text" hint going red — never a separate box.
function matchForward(startPointer: number, heardTokens: string[]): { pointer: number; hadStray: boolean } {
  let pointer = startPointer
  let hadStray = false
  for (const h of heardTokens) {
    const windowEnd = Math.min(sentenceTargetTokens.value.length, pointer + 1 + LOOKAHEAD)
    let matched = false
    for (let i = pointer; i < windowEnd; i++) {
      if (sentenceTargetTokens.value[i] === h) {
        pointer = i + 1
        matched = true
        break
      }
    }
    if (!matched) hadStray = true
  }
  return { pointer, hadStray }
}

// Live, non-committing preview of how far a still-in-progress partial would advance the
// pointer — this is what makes the karaoke highlight move word-by-word as the trainee talks,
// not just jump once per final.
const tentativePointer = computed(() => {
  if (step.value !== 'reading' || !partialText.value) return readPointer.value
  return matchForward(readPointer.value, tokenize(partialText.value)).pointer
})

const MISMATCH_FLASH_MS = 1800
const mismatchActive = ref(false)
let mismatchClearTimer: ReturnType<typeof setTimeout> | null = null

function flashMismatch() {
  mismatchActive.value = true
  if (mismatchClearTimer) clearTimeout(mismatchClearTimer)
  mismatchClearTimer = setTimeout(() => {
    mismatchActive.value = false
  }, MISMATCH_FLASH_MS)
}

function clearMismatch() {
  mismatchActive.value = false
  if (mismatchClearTimer) clearTimeout(mismatchClearTimer)
  mismatchClearTimer = null
}

function advanceReading(finalText: string) {
  const { pointer, hadStray } = matchForward(readPointer.value, tokenize(finalText))
  readPointer.value = pointer
  if (hadStray) flashMismatch()
}

const sentenceDone = computed(() => readPointer.value >= sentenceTargetTokens.value.length)

// Item 3: auto-advance a beat after the sentence completes (via matching, not a manual click),
// so the trainee sees it finish before the next one appears. "Siguiente"/"Next" stays a manual
// override for a stuck sentence — it's never disabled, it can skip early too.
const AUTO_ADVANCE_DELAY_MS = 800
let autoAdvanceTimer: ReturnType<typeof setTimeout> | null = null

function clearAutoAdvanceTimer() {
  if (autoAdvanceTimer) clearTimeout(autoAdvanceTimer)
  autoAdvanceTimer = null
}

watch(sentenceDone, (done) => {
  if (done && step.value === 'reading') {
    clearAutoAdvanceTimer()
    autoAdvanceTimer = setTimeout(() => {
      autoAdvanceTimer = null
      if (step.value === 'reading' && sentenceDone.value) nextSentence()
    }, AUTO_ADVANCE_DELAY_MS)
  } else {
    clearAutoAdvanceTimer()
  }
})

// Item 4: persist which sentence this player has reached for the current lang, so a reload
// resumes here instead of restarting (see loadTrainingProgress in onMounted). Best-effort —
// never surfaces to the UI, same policy as the sample POSTs.
function saveProgress(idx: number, completed: boolean) {
  if (!player.value) return
  putTrainingProgress({ playerId: player.value.id, lang: lang.value, sentenceIndex: idx, completed }).catch(() => {
    /* best-effort — a failed save just means a reload won't resume exactly here */
  })
}

function nextSentence() {
  if (step.value !== 'reading') return
  clearMismatch()
  clearAutoAdvanceTimer()
  if (sentenceIndex.value + 1 < totalSentences.value) {
    sentenceIndex.value += 1
    readPointer.value = 0
    partialText.value = ''
    saveProgress(sentenceIndex.value, false)
  } else {
    finishFlow()
  }
}

const overallProgressPct = computed(() => {
  if (step.value === 'done') return 100
  const perSentence = 100 / totalSentences.value
  const fraction = step.value === 'reading' && sentenceTargetTokens.value.length ? readPointer.value / sentenceTargetTokens.value.length : 0
  return Math.min(100, Math.round(sentenceIndex.value * perSentence + fraction * perSentence))
})

// ---------------------------------------------------------------------------------------------
// Noise baseline (item 5). A few seconds of silence captured from kmb-stt's rolling audio ring
// (server-side change in kmb_stt.py: {"type":"capture_noise"} -> {"type":"noise_sample",
// "audioB64":...} — the ring is fed on every chunk regardless of VAD/speech, so this works with
// the room genuinely silent). Stored as its own voice_samples row — source "enrollment" (so
// retention keeps it forever, same as reading samples, unlike the pruned "kiosk" passive rows)
// with command "noise_baseline" so it's distinguishable from spoken samples. Not consumed by
// anything yet — see DESIGN.md §11's "way later" noise-cancelling notes.
const NOISE_CAPTURE_SECONDS = 4
const noiseSecondsLeft = ref(NOISE_CAPTURE_SECONDS)
let noiseCountdownTimer: ReturnType<typeof setInterval> | null = null
let noiseCaptureTimeout: ReturnType<typeof setTimeout> | null = null
let awaitingNoiseSample = false

function clearNoiseTimers() {
  if (noiseCountdownTimer) clearInterval(noiseCountdownTimer)
  if (noiseCaptureTimeout) clearTimeout(noiseCaptureTimeout)
  noiseCountdownTimer = null
  noiseCaptureTimeout = null
  awaitingNoiseSample = false
}

function requestNoiseCapture() {
  if (ws && ws.readyState === WebSocket.OPEN) {
    awaitingNoiseSample = true
    ws.send(JSON.stringify({ type: 'capture_noise' }))
    // Best-effort: if kmb-stt never replies (old build without this message type), don't strand
    // the flow on the silence screen forever.
    setTimeout(() => {
      if (awaitingNoiseSample) {
        awaitingNoiseSample = false
        beginReadingStep()
      }
    }, 2000)
  } else {
    beginReadingStep()
  }
}

function postNoiseSample(audioB64: string) {
  if (!player.value) return
  postVoiceSample({
    lang: lang.value,
    text: '',
    command: 'noise_baseline',
    playerId: player.value.id,
    spkVector: null,
    spkModelVersion: null,
    audioB64,
    source: 'enrollment', // kept forever by retention, like reading samples — never pruned
  }).catch(() => {
    /* corpus collection is best-effort — never surfaces to the UI */
  })
}

// ---------------------------------------------------------------------------------------------
// Enrollment: delegates to the shared stt.ts session (the one pipeline that actually POSTs
// reading samples) — this page just starts/stops it and shows progress. A samples target of 20
// is a reasonable "enough for a decent centroid" number for a running-mean average, not a hard
// requirement — training can be stopped earlier or kept running longer.
const SAMPLE_TARGET = 20
const training = computed(() => stt.enrollPlayerId != null && stt.enrollPlayerId === player.value?.id)
const progressPct = computed(() => Math.min(100, Math.round((stt.enrollCount / SAMPLE_TARGET) * 100)))

function startTrainingFlow() {
  if (!player.value || connectionState.value !== 'open') return
  // Item 4: only reset to sentence 0 on an explicit "Train again" after finishing — the FIRST
  // Start of a session keeps whatever sentenceIndex loadTrainingProgress() resumed us at
  // (0 if there was nothing to resume), so it actually resumes instead of restarting.
  if (step.value === 'done') sentenceIndex.value = 0
  readPointer.value = 0
  partialText.value = ''
  clearMismatch()
  step.value = 'noise'
  noiseSecondsLeft.value = NOISE_CAPTURE_SECONDS
  noiseCountdownTimer = setInterval(() => {
    noiseSecondsLeft.value = Math.max(0, noiseSecondsLeft.value - 1)
  }, 1000)
  noiseCaptureTimeout = setTimeout(() => {
    if (noiseCountdownTimer) clearInterval(noiseCountdownTimer)
    noiseCountdownTimer = null
    requestNoiseCapture()
  }, NOISE_CAPTURE_SECONDS * 1000)
}

function beginReadingStep() {
  if (!player.value) return
  startEnrollment(player.value.id, lang.value)
  step.value = 'reading'
}

// Round-17 (live-testing fix, rider 2): a short grace window after Stop/Finish so the final
// recognition event for whatever utterance was mid-flight at that instant still posts as a
// labeled, permanent enrollment sample instead of racing stopEnrollment and landing unlabeled
// (see stt.ts's stopEnrollment(graceMs) doc comment). After the grace window, do a full
// profile-centroid rebuild from everything now stored — belt-and-suspenders on top of the
// per-insert folding the server already does, so a session's end (by Stop OR by finishing all
// sentences) always leaves the profile reflecting every sample that made it in.
const ENROLLMENT_STOP_GRACE_MS = 1500

function stopEnrollmentAndRebuild() {
  const playerId = player.value?.id
  if (!training.value || playerId == null) return
  stopEnrollment(ENROLLMENT_STOP_GRACE_MS)
  setTimeout(() => {
    rebuildVoiceProfile(playerId).catch(() => {
      /* best-effort — per-insert folding already keeps the centroid roughly current */
    })
  }, ENROLLMENT_STOP_GRACE_MS + 250) // after the grace-delayed stop actually clears
}

function finishFlow() {
  step.value = 'done'
  saveProgress(totalSentences.value, true)
  stopEnrollmentAndRebuild()
}

function stopFlow() {
  clearNoiseTimers()
  clearAutoAdvanceTimer()
  stopEnrollmentAndRebuild()
  step.value = 'idle'
  // sentenceIndex is left as-is (not reset to 0) — Stop keeps whatever was last completed
  // (already persisted via saveProgress on each nextSentence), consistent with item 4: a
  // reload resumes exactly where an in-app Stop would have left the trainee too.
  readPointer.value = 0
  partialText.value = ''
  clearMismatch()
}

function primaryAction() {
  if (step.value === 'idle' || step.value === 'done') startTrainingFlow()
  else stopFlow()
}

function switchLang(l: Lang) {
  if (step.value !== 'idle') return // avoid desyncing an in-progress flow
  lang.value = l
}

// Item 4: resume at the right sentence (and language) after a reload — pick the most-advanced
// NOT-yet-completed row (there's at most one per lang, es + en). A finished lang isn't
// resumable — starting it again should begin at sentence 0, same as "Train again".
async function loadTrainingProgress() {
  if (!player.value) return
  try {
    const { items } = await getTrainingProgress(player.value.id)
    const resumable = items
      .filter((it): it is typeof it & { lang: Lang } => (it.lang === 'es' || it.lang === 'en') && !it.completed)
      .sort((a, b) => b.sentenceIndex - a.sentenceIndex)[0]
    if (resumable) {
      lang.value = resumable.lang
      sentenceIndex.value = Math.min(Math.max(resumable.sentenceIndex, 0), totalSentences.value - 1)
    }
  } catch {
    /* best-effort — no saved progress, or offline: starts fresh at sentence 0 */
  }
}

onMounted(async () => {
  await loadPlayer()
  await loadTrainingProgress()
  connect()
})
onUnmounted(() => {
  closedByUs = true
  if (retryTimer) clearTimeout(retryTimer)
  clearNoiseTimers()
  clearAutoAdvanceTimer()
  clearMismatch()
  ws?.close()
  // Leaving the page mid-session stops enrollment too — no orphaned background training a
  // player forgot was running. Uses the same grace+rebuild path as Stop/Finish (round-17):
  // stt.ts's own app-wide connection outlives this component, so the delayed clear still runs
  // and still catches a trailing final that was mid-flight when the player tapped Back.
  stopEnrollmentAndRebuild()
})
</script>

<template>
  <div class="flex-1 min-h-0 flex flex-col overflow-y-auto p-4 gap-4">
    <div class="flex items-center gap-3 shrink-0">
      <button
        type="button"
        class="tap-target w-10 h-10 min-h-0 min-w-0 shrink-0 rounded-full active:bg-neutral-800 flex items-center justify-center text-neutral-400"
        aria-label="Back"
        @click="goBack"
      >
        <svg viewBox="0 0 24 24" class="w-5 h-5" fill="currentColor"><path :d="mdiArrowLeft" /></svg>
      </button>
      <h1 class="text-lg font-bold flex-1">Train my voice{{ player ? ' — ' + player.displayName : '' }}</h1>
      <span class="w-2 h-2 rounded-full" :class="connectionState === 'open' ? 'bg-emerald-400' : 'bg-neutral-600'" :title="connectionState === 'open' ? 'Mic connected' : 'Mic offline'"></span>
    </div>

    <p v-if="loadError" class="text-neutral-500">Could not load this player.</p>

    <template v-else>
      <!-- Language toggle — only changeable before a flow starts (item: don't desync mid-flow). -->
      <div class="flex gap-2 shrink-0">
        <button
          type="button"
          class="tap-target px-4 rounded-xl font-semibold text-sm disabled:opacity-40"
          :class="lang === 'es' ? 'bg-emerald-600' : 'bg-neutral-800 active:bg-neutral-700'"
          :disabled="step !== 'idle'"
          @click="switchLang('es')"
        >
          Español
        </button>
        <button
          type="button"
          class="tap-target px-4 rounded-xl font-semibold text-sm disabled:opacity-40"
          :class="lang === 'en' ? 'bg-emerald-600' : 'bg-neutral-800 active:bg-neutral-700'"
          :disabled="step !== 'idle'"
          @click="switchLang('en')"
        >
          English
        </button>
      </div>

      <!-- Merged panel (round-17, item 1; reworked round-18, item 2): the guided text itself IS
           the live-recognition display — a single karaoke component, no separate transcript or
           mismatch box. Audio level stays as a thin always-on strip so the room can see the mic
           is alive even before Start. -->
      <div class="rounded-2xl bg-neutral-900 border border-neutral-800 p-4 flex flex-col gap-3">
        <div class="flex items-center gap-3">
          <span class="text-xs text-neutral-500 font-semibold shrink-0 w-10">Level</span>
          <div class="flex-1 h-2 rounded-full bg-neutral-800 overflow-hidden">
            <div class="h-full bg-emerald-500 transition-all duration-75" :style="{ width: `${Math.min(100, rms * 300)}%` }" />
          </div>
        </div>

        <!-- Item 4: this bar reflects sentenceIndex/readPointer in every step, including idle —
             so a resumed session shows real progress before Start is even pressed. -->
        <div class="flex items-center gap-3">
          <span class="text-xs text-neutral-500 font-semibold shrink-0 w-10">{{ lang === 'es' ? 'Frase' : 'Sentence' }}</span>
          <div class="flex-1 h-2 rounded-full bg-neutral-800 overflow-hidden">
            <div class="h-full bg-sky-500 transition-all duration-300" :style="{ width: `${overallProgressPct}%` }" />
          </div>
          <span class="text-xs font-mono text-neutral-500 shrink-0">{{ Math.min(sentenceIndex + 1, totalSentences) }}/{{ totalSentences }}</span>
        </div>

        <!-- idle: dimmed preview + explicit call-to-action (round-17 rider fix: recognition
             must not run, and must not LOOK like it's running, before Start). Item 4: shows the
             RESUMED sentence, not always the first one. -->
        <div v-if="step === 'idle'" class="flex flex-col gap-2">
          <p class="text-lg leading-relaxed text-neutral-600 select-none">{{ SENTENCES[lang][sentenceIndex] }}</p>
          <p v-if="sentenceIndex > 0" class="text-xs text-neutral-500">
            {{ lang === 'es' ? `Reanudando en la frase ${sentenceIndex + 1} de ${totalSentences}.` : `Resuming at sentence ${sentenceIndex + 1} of ${totalSentences}.` }}
          </p>
          <p class="text-sm font-semibold text-amber-400">{{ lang === 'es' ? 'Pulsa «Empezar entrenamiento» para comenzar.' : 'Press "Start training" to begin.' }}</p>
        </div>

        <!-- noise: silence baseline capture. -->
        <div v-else-if="step === 'noise'" class="flex flex-col items-center gap-2 py-4 text-center">
          <p class="text-lg font-semibold">{{ lang === 'es' ? 'Mantén silencio unos segundos…' : 'Stay silent for a few seconds…' }}</p>
          <p class="text-sm text-neutral-500">{{ lang === 'es' ? 'Estamos midiendo el ruido de la sala.' : "We're measuring the room's background noise." }}</p>
          <p class="text-3xl font-mono text-emerald-400">{{ noiseSecondsLeft }}</p>
        </div>

        <!-- reading (item 2, single karaoke component): words turn green progressively as
             spoken — confirmed (readPointer, from finals) solid green, in-flight (tentativePointer,
             from the live partial) a lighter green preview. A mismatch shakes + tints the
             not-yet-read words red for a beat and turns the standing hint red too — no separate
             red strip anywhere. -->
        <div v-else-if="step === 'reading'" class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <span class="text-xs font-bold text-neutral-500 uppercase tracking-wide">
              {{ lang === 'es' ? `Frase ${sentenceIndex + 1} de ${totalSentences}` : `Sentence ${sentenceIndex + 1} of ${totalSentences}` }}
            </span>
            <span v-if="sentenceDone" class="text-xs font-bold text-emerald-400 uppercase tracking-wide">{{ lang === 'es' ? 'Lista' : 'Ready' }}</span>
          </div>
          <p class="text-lg leading-relaxed" :class="{ 'animate-shake': mismatchActive }">
            <span
              v-for="(w, i) in sentenceWords"
              :key="i"
              :class="i < readPointer
                ? 'text-emerald-400'
                : mismatchActive
                  ? 'text-red-400'
                  : i < tentativePointer
                    ? 'text-emerald-300/70'
                    : 'text-neutral-400'"
            >{{ w }}{{ ' ' }}</span>
          </p>
          <p class="text-xs" :class="mismatchActive ? 'text-red-400 font-semibold' : 'text-neutral-600'">
            {{ lang === 'es' ? 'Lee el texto en pantalla.' : 'Read the text on screen.' }}
          </p>
          <button
            type="button"
            class="tap-target rounded-xl font-semibold bg-sky-600 active:bg-sky-700"
            @click="nextSentence"
          >
            {{ sentenceIndex + 1 < totalSentences ? (lang === 'es' ? 'Siguiente' : 'Next') : (lang === 'es' ? 'Terminar' : 'Finish') }}
          </button>
        </div>

        <!-- done: all sentences read. -->
        <div v-else class="flex flex-col items-center gap-2 py-4 text-center">
          <p class="text-lg font-semibold text-emerald-400">{{ lang === 'es' ? '¡Completado!' : 'All done!' }}</p>
          <p class="text-sm text-neutral-500">{{ lang === 'es' ? `Leíste ${totalSentences} frases.` : `You read ${totalSentences} sentences.` }}</p>
        </div>
      </div>

      <!-- Enrollment progress + the single flow control (round-17: one button drives idle →
           noise → reading → done, per the state-machine fix). -->
      <div class="rounded-2xl bg-neutral-900 border border-neutral-800 p-4 flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <span class="text-xs font-bold text-neutral-500 uppercase tracking-wide">Enrollment progress</span>
          <span class="font-mono text-sm">{{ stt.enrollCount }} / {{ SAMPLE_TARGET }} samples</span>
        </div>
        <div class="h-3 rounded-full bg-neutral-800 overflow-hidden">
          <div class="h-full bg-emerald-500 transition-all duration-300" :style="{ width: `${progressPct}%` }" />
        </div>
        <button
          type="button"
          class="tap-target rounded-xl font-semibold"
          :class="step === 'idle' || step === 'done' ? 'bg-emerald-600 active:bg-emerald-700 disabled:opacity-40' : 'bg-red-700 active:bg-red-800'"
          :disabled="step === 'idle' && connectionState !== 'open'"
          @click="primaryAction"
        >
          <template v-if="step === 'idle'">{{ lang === 'es' ? 'Empezar entrenamiento' : 'Start training' }}</template>
          <template v-else-if="step === 'done'">{{ lang === 'es' ? 'Entrenar de nuevo' : 'Train again' }}</template>
          <template v-else>{{ lang === 'es' ? 'Detener' : 'Stop training' }}</template>
        </button>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* Item 2: the mismatch shake on the karaoke sentence — a subtle nudge, not a jolt. */
@keyframes shake {
  10%, 90% { transform: translateX(-1px); }
  20%, 80% { transform: translateX(2px); }
  30%, 50%, 70% { transform: translateX(-3px); }
  40%, 60% { transform: translateX(3px); }
}
.animate-shake {
  animation: shake 0.4s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
}
</style>
